import { createFileRoute, Link } from "@tanstack/react-router";
import {
	CalendarClock,
	Eye,
	GitBranch,
	LayoutDashboard,
	MessageSquare,
	MoveHorizontal,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/features/calendar";

export const Route = createFileRoute(path)({
	component: CalendarFeature,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher | Unified Social Calendar",
			description:
				"One calendar across every connected platform. Drag, edit, regenerate, and approve posts from a single view.",
		}),
});

function CalendarFeature() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Feature: unified calendar"
				title="One calendar. Every channel. Every queued post."
				description="Stop juggling per-platform queues. AgentReacher's calendar is the single source of truth for everything scheduled across every connected account."
			/>

			<MarketingSection
				title="What lives in the calendar"
				description="Everything queued, everywhere, in one view."
			>
				<FeatureGrid
					features={[
						{
							icon: LayoutDashboard,
							title: "Every platform, one view",
							description:
								"Bluesky, X, LinkedIn, Instagram, TikTok, and the rest live in the same calendar.",
						},
						{
							icon: MoveHorizontal,
							title: "Drag to reschedule",
							description:
								"Move posts between days or platforms. The agent re-validates timing.",
						},
						{
							icon: CalendarClock,
							title: "Time-zone aware",
							description:
								"Each post shows in your local time but ships in the audience's window.",
						},
						{
							icon: Eye,
							title: "Status at a glance",
							description:
								"Drafted, scheduled, processing, published, failed  -  every state visible inline.",
						},
						{
							icon: GitBranch,
							title: "Per-platform variations",
							description:
								"See the LinkedIn vs. X version of the same post side by side.",
						},
						{
							icon: MessageSquare,
							title: "Approvals inline",
							description:
								"Reviewers approve directly from the calendar. No separate review queue to manage.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						Related:{" "}
						<Link
							to="/features/ai-scheduling"
							className="text-primary transition-colors hover:text-primary/80"
						>
							AI scheduling
						</Link>
						,{" "}
						<Link
							to="/features/team-approval"
							className="text-primary transition-colors hover:text-primary/80"
						>
							team approvals
						</Link>
						,{" "}
						<Link
							to="/docs/calendar"
							className="text-primary transition-colors hover:text-primary/80"
						>
							calendar docs
						</Link>
						.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
